/* ═══════════════════════════════════════════════════════════════════════
   Checking an email address, in the browser.

   A copy of the server's emails.ts, and deliberately so: the server is the
   authority and re-runs every rule below, but a form that only tells you
   about a typo after a round trip is a form that tells you too late. The
   duplication is small, both copies are pure, and the failure mode if they
   drift is a message appearing a moment later rather than a bad address
   getting through.

   None of this is proof. It catches mistakes while the person who made
   one is still looking at the field; the verification email is what
   actually establishes that an address exists and belongs to them.
   ═══════════════════════════════════════════════════════════════════════ */

export function emailProblem(raw: string): string | null {
  const email = raw.trim();

  if (!email) return "We'll need an email address.";
  // Somebody pasted a name and an address together, or a mailto: link.
  if (/\s/.test(email)) return "That has a space in it — just the address on its own.";
  if (/^mailto:/i.test(email)) return "Just the address, without the mailto: part.";
  if (/[<>(),;:\\"[\]]/.test(email)) return "That has characters an address can't contain.";

  const at = email.split("@");
  if (at.length !== 2) {
    return at.length < 2 ? "That's missing the @." : "That has more than one @ in it.";
  }
  const [local, domain] = at as [string, string];

  if (!local) return "There's nothing before the @.";
  if (local.length > 64) return "The part before the @ is too long.";
  if (email.length > 254) return "That address is longer than addresses can be.";

  if (!domain) return "There's nothing after the @.";
  if (!domain.includes(".")) return "The part after the @ needs a dot in it — like gmail.com.";
  // ".." is the one people produce by pasting badly, and it is never valid.
  if (email.includes("..")) return "There are two dots together in there.";
  if (local.startsWith(".") || local.endsWith(".")) return "That has a dot in an odd place.";
  if (domain.startsWith(".") || domain.startsWith("-")) return "The part after the @ starts oddly.";
  if (domain.endsWith(".") || domain.endsWith("-")) return "The part after the @ ends oddly.";
  if (!/^[a-z0-9.-]+$/i.test(domain)) return "The part after the @ has characters it can't have.";

  const tld = domain.slice(domain.lastIndexOf(".") + 1);
  if (tld.length < 2) return "That address is missing the end of its domain.";
  if (!/^[a-z]{2,}$/i.test(tld)) return "The end of that domain doesn't look right.";

  return null;
}

/**
 * A likely correction, or null.
 *
 * Never blocks. Shown beside the field so somebody can glance at it and
 * carry on, because being told "did you mean" about an address you typed
 * correctly is a mild irritation and being stopped by it is not.
 */
export function suggestEmail(raw: string): string | null {
  const email = raw.trim().toLowerCase();
  const domain = email.split("@")[1];
  if (!domain) return null;

  /* Only the ones that are overwhelmingly a slip. A longer list starts
     guessing at real domains, and a wrong suggestion is worse than none —
     people take them. */
  const typos: Record<string, string> = {
    "gmial.com": "gmail.com",
    "gmai.com": "gmail.com",
    "gmail.co": "gmail.com",
    "gmail.cmo": "gmail.com",
    "gnail.com": "gmail.com",
    "hotmial.com": "hotmail.com",
    "hotmai.com": "hotmail.com",
    "hotnail.com": "hotmail.com",
    "yahooo.com": "yahoo.com",
    "yaho.com": "yahoo.com",
    "outlok.com": "outlook.com",
    "outloook.com": "outlook.com",
    "icloud.co": "icloud.com",
    "bigpon.com.au": "bigpond.com.au",
  };
  if (typos[domain]) return email.replace(/@.*$/, `@${typos[domain]}`);

  /* The near-misses of ".com" — a fingertip landing one key over. Left as
     a nudge rather than a rewrite, since .co and .co.uk are real. */
  if (/\.(con|cmo|comm|xom)$/.test(domain)) {
    return email.replace(/\.(con|cmo|comm|xom)$/, ".com");
  }
  return null;
}
